import { ChangeDetectionStrategy, Component, inject, linkedSignal, signal } from '@angular/core';
import { FormField, disabled, email, form, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { finalize } from 'rxjs';
import { Messenger, UserService } from 'src/app/service';

import { Roles, Teams, User, UserCapacities, UserStates } from '../schemas';

export interface UserFormData {
  user?: User;
  title?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormField,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressBarModule,
  ],
  templateUrl: './user-form.html',
})
export class UserForm {
  data = (inject(MAT_DIALOG_DATA) as UserFormData) || {};

  hostDialog = inject(MatDialogRef);
  users = inject(UserService);
  messenger = inject(Messenger);

  pending = signal(false);

  model = linkedSignal(() => {
    return {
      Email: '',
      FullName: '',
      Role: '',
      State: 'active',
      Team: '',
      Capacity: '',
      SalesCapacity: UserCapacities.Sales,
      CkanCapacity: UserCapacities.Ckan,
      DrupalCapacity: UserCapacities.Drupal,
      JsCapacity: UserCapacities.Js,
      OtherCapacity: UserCapacities.Other,
      JiraId: '',
      ...this.data.user,
    } as User;
  });

  form = form(this.model, (schema) => {
    required(schema.Email);
    email(schema.Email);
    disabled(schema.Email, () => !!this.data.user);
    required(schema.FullName);
    required(schema.Role);
    required(schema.State);
  });

  States = Object.keys(UserStates).map((k) => ({
    value: k,
    label: UserStates[k],
  }));
  Roles = Object.keys(Roles).map((k) => ({
    value: k,
    label: Roles[k],
  }));
  Teams = Object.keys(Teams).map((k) => ({
    value: k,
    label: Teams[k],
  }));

  save() {
    this.pending.set(true);

    const data = this.form().value();

    this.users
      .save(data, !!this.data.user)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: (data: User) => {
          this.hostDialog.close(data);
        },
        error: this.messenger.handleHttpError,
      });
  }
}
